<script setup lang="ts">
import { computed, inject, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import { Z_INDEX } from '@/utils/zIndex'

/**
 * Mobile presentation for a single detail view (a collection POI or a tour waypoint): a draggable
 * bottom sheet (peek / mid / full) so the map and the detail stay visible together. Desktop keeps
 * its floating side panel; this is the small-screen replacement for the old centered POI modal and
 * the waypoint detail that used to render inside the Route sheet's Details tab. The host owns the
 * content (slots in `POIDetailPanel`) and the open/close state.
 */
defineOptions({ name: 'DetailSheet' })

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

// Same bottom offset the other detail sheets use (RouteDetailSheet, PoiBrowseSheet) — sits at the
// screen edge when the tab bar is hidden (detail level), not floating above a gap.
const floatingSheetBottom = inject<Ref<string>>(
  'floatingSheetBottomOffset',
  ref(FLOATING_SHEET_BOTTOM_OFFSET),
)

const viewportHeight = ref(
  typeof window === 'undefined' ? 844 : (window.visualViewport?.height ?? window.innerHeight),
)
const updateViewportHeight = () => {
  viewportHeight.value = window.visualViewport?.height ?? window.innerHeight
}

const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  const full = mobileSheetFullHeight(height)
  return {
    // Peek still shows the name + type + primary actions so the sheet is useful when collapsed.
    peek: Math.min(280, full - 40),
    mid: Math.min(460, full - 40),
    full,
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: 'mid',
  // Swipe the sheet down past peek to dismiss (as well as the panel's own close button).
  onDismiss: () => emit('close'),
})

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(floatingSheetBottom.value),
  bottom: floatingSheetBottom.value,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition: sheet.isDragging.value ? 'none' : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

onMounted(() => window.visualViewport?.addEventListener('resize', updateViewportHeight))
onBeforeUnmount(() => window.visualViewport?.removeEventListener('resize', updateViewportHeight))
useEventListener(window, 'resize', updateViewportHeight)
</script>

<template>
  <Transition name="detail-sheet">
    <div
      v-if="props.open"
      class="pointer-events-auto fixed left-3 right-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white shadow-[0_-8px_30px_rgba(15,23,42,0.14)]"
      :style="sheetStyle"
      data-testid="detail-sheet"
    >
      <!-- Drag handle -->
      <div
        class="flex shrink-0 touch-none justify-center bg-white py-1.5"
        @pointerdown="sheet.onPointerDown"
        @pointermove="sheet.onPointerMove"
        @pointerup="sheet.onPointerUp"
        @pointercancel="sheet.onPointerUp"
      >
        <div class="h-1.5 w-12 rounded-full bg-gray-300"></div>
      </div>

      <div class="min-h-0 flex-1 overflow-hidden">
        <slot :close="() => emit('close')" />
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.detail-sheet-enter-active,
.detail-sheet-leave-active {
  transition:
    transform 200ms ease,
    opacity 200ms ease;
}
.detail-sheet-enter-from,
.detail-sheet-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .detail-sheet-enter-active,
  .detail-sheet-leave-active {
    transition: none;
  }
}
</style>
